import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import Logo12EN12 from '../components/Logo12EN12';
import { pedirFraseAlEntrar } from '../components/FraseAlEntrar';
import { mensajeDeError } from '../lib/mensajeDeError';
import { hayCuentaDeBaja, olvidarCuentaDeBaja } from '../lib/cuentaDeBaja';
import { destinoTrasEntrar } from '../lib/navegacion';
import SegundoFactorPaso from '../components/SegundoFactorPaso';

// Aquí había una copia del logo hecha a mano, con la flecha colgando por encima del suelo de
// las letras. Se usa el de siempre (14-08-2026): así el logo es uno solo y se arregla en un
// sitio. `tone="dark"` porque esta pantalla es negra siempre, no sigue al tema.

const AuthPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Solo lo imprescindible para tener cuenta.
    const [formData, setFormData] = useState({ email: '', password: '' });
    // LA CUENTA DADA DE BAJA (12-10). El servidor contesta 403 al entrar y el aviso era un
    // toast de 4 segundos que solo decía que contactara con su entrenador: quien no lo leía
    // a tiempo probaba a recuperar la contraseña (no le llega nada) o a registrarse otra vez
    // (el correo ya existe), sin salida. Se queda fijo en la pantalla y dice qué hacer.
    // Y TAMBIÉN A QUIEN SE LE DIO DE BAJA CON LA APP ABIERTA (14-10, BAJ-12): llegaba aquí
    // sin una frase, como si se le hubiera caducado la sesión. El recado lo deja el 403
    // (`lib/cuentaDeBaja`).
    const [cuentaDeBaja, setCuentaDeBaja] = useState(() => hayCuentaDeBaja());
    useEffect(() => { olvidarCuentaDeBaja(); }, []);
    // El segundo paso de los admins (28-10): `{segundoPaso, ticket}` tras la contraseña.
    const [segundoPaso, setSegundoPaso] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setCuentaDeBaja(false);
        
        try {
            const resultado = await login(formData.email, formData.password);
            if (resultado?.segundoPaso) setSegundoPaso(resultado);
            else entrar(resultado);
        } catch (error) {
            if (error?.response?.status === 403) {
                setCuentaDeBaja(true);
                return;
            }
            toast.error(mensajeDeError(error, 'Error de autenticación'));
        } finally {
            setLoading(false);
        }
    };

    const entrar = (user) => {
        // A donde iba (el botón de un correo) o a su panel: lo mismo que decide el
        // PublicRoute, que navega a la vez (`lib/navegacion.destinoTrasEntrar`).
        const destino = destinoTrasEntrar(user);
        if (user.role === 'admin' || user.role === 'trainer') {
            toast.success(`¡Hola, ${user.name}!`);
            navigate(destino);
        } else {
            // AL CLIENTE, LA FRASE DEL DÍA (15-10): sale a pantalla completa nada más
            // entrar y se desvanece sobre el Inicio. La pinta el layout del cliente
            // (components/FraseAlEntrar), que es lo que queda montado tras el login.
            pedirFraseAlEntrar();
            navigate(destino);
        }
    };

    return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 relative overflow-hidden">
            {/* Background Image - Gohan Dark */}
            <div 
                className="absolute inset-0 opacity-[0.15]"
                style={{
                    backgroundImage: `url('/gohan-dark.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            />
            
            {/* Content */}
            <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
                {/* Logo */}
                <div className="mb-10">
                    <Logo12EN12 size="lg" tone="dark" />
                </div>
                
                {/* Title */}
                {/* «Login» era la única palabra en inglés que le quedaba a la app. Y el
                    subtítulo del acceso sobraba: hay dos campos y un botón, se ve solo
                    (Jesús, 11-08). */}
                <h1 className="text-white text-3xl font-bold mb-10 tracking-tight">Entrar</h1>
                
                {segundoPaso ? (
                    <SegundoFactorPaso email={formData.email} ticket={segundoPaso.ticket} paso={segundoPaso.segundoPaso}
                        onEntrar={entrar} onVolver={() => setSegundoPaso(null)} />
                ) : (
                <form onSubmit={handleSubmit} className="w-full space-y-4">
                    {/* Ni nombre ni teléfono: crear cuenta es correo y contraseña.
                        Pedirle el teléfono a quien viene a por un dato gratis espanta a
                        la mitad. El nombre se pide después, con algo suyo ya delante, y
                        el teléfono al comprar, que es cuando hace falta llamar. */}

                    <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-brand/20 flex items-center justify-center">
                            <Mail className="w-3 h-3 text-brand" />
                        </div>
                        <Input
                            type="email"
                            placeholder="Email"
                            className="pl-12 h-14 bg-bg-input-dark border-0 text-white placeholder:text-white/40 rounded-xl focus:ring-2 focus:ring-brand"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            data-testid="login-email"
                        />
                    </div>
                    
                    <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-brand/20 flex items-center justify-center">
                            <Lock className="w-3 h-3 text-brand" />
                        </div>
                        <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Contraseña"
                            className="pl-12 pr-12 h-14 bg-bg-input-dark border-0 text-white placeholder:text-white/40 rounded-xl focus:ring-2 focus:ring-brand"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                            minLength={6}
                            data-testid="login-password"
                        />
                        <button
                            type="button"
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                            data-testid="toggle-password"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    
                    {/* Este botón existía y lo único que hacía era decirle que escribiera
                        a su entrenador por WhatsApp. Ahora lleva a recuperarla por correo:
                        con el registro abierto hay gente sin entrenador a quien escribir. */}
                    <button
                        type="button"
                        className="text-brand text-sm hover:underline w-full text-right"
                        onClick={() => navigate('/recuperar')}
                        data-testid="login-olvidada"
                    >
                        ¿Olvidaste tu contraseña?
                    </button>
                    
                    {cuentaDeBaja && (
                        <div role="alert" data-testid="cuenta-de-baja"
                            className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 text-left">
                            <p className="font-semibold">Esta cuenta está desactivada.</p>
                            <p className="mt-1 text-amber-100/80">
                                No puedes entrar, y recuperar la contraseña o registrarte otra
                                vez con este correo no la reactiva. Escribe a tu entrenador o al
                                equipo de 12EN12, por donde hables con ellos, y pide que te la
                                reactiven: tus datos siguen guardados.
                            </p>
                        </div>
                    )}

                    <Button 
                        type="submit" 
                        className="w-full h-14 bg-brand hover:bg-brand-dark text-white font-bold text-lg rounded-full transition-all duration-200 shadow-lg shadow-brand/30"
                        disabled={loading}
                        data-testid="login-submit"
                    >
                        {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : 'Entrar'}
                    </Button>
                </form>
                )}
                
                {/* FUERA «¿No tienes cuenta? Regístrate» (15-10): esta pantalla es solo para
                    entrar. Las cuentas no se crean desde aquí. */}

                {/* Footer */}
                <p className="text-white/30 text-xs mt-10 text-center">
                    © 2026 12EN12 Training System
                </p>
            </div>
        </div>
    );
};

export default AuthPage;
